import differenceInDays from "date-fns/differenceInDays";
import type { FunctionComponentWithRef, ReactNode } from "react";
import { graphql, useFragment } from "react-relay";

import { parseDate } from "shared/lib/utils";

import { Share } from "scmp-app/components/article/article-2.0-action-widget/share";
import { useSeriesArticleSectionTag } from "scmp-app/components/article/article-render/series-article/hooks";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { seriesBannerContent$key } from "scmp-app/queries/__generated__/seriesBannerContent.graphql";

import {
  ActionWidget,
  BorderedContainer,
  Container,
  Content,
  Headline,
  NewTag,
  SeriesArticleSectionTag,
  StyledBaseLink,
  Summary,
  SummaryAndActionWidget,
  UpdatedDate,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  actionWidget: ReactNode;
  reference: seriesBannerContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  actionWidget,
  className,
  ref,
  reference,
  topicLinkVariant,
}) => {
  const content = useFragment(
    graphql`
      fragment seriesBannerContent on Content {
        ...hooksSeriesArticleSectionTagArticle
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withCoverImage: true
            withCoverImageSize540x360: true
            withCoverImageLandscape250x99: true
            withHeadline: true
            withSummary: true
            withUpdatedDate: true
          )
        publishedDate
        ...shareArticle20Content
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(content, topicLinkVariant);
  const headline = providers.headline({
    componentTag: "h1",
    hideTooltip: true,
    preferSocialHeadline: true,
  });
  const seriesArticleSectionTag = useSeriesArticleSectionTag(content);

  const isShowNewSeries = () => {
    if (!content.publishedDate) return false;
    const diff = differenceInDays(new Date(), parseDate(content.publishedDate));
    return diff <= 30;
  };

  return (
    <Container className={className} ref={ref}>
      <Content>
        <BorderedContainer>
          <SeriesArticleSectionTag>
            <StyledBaseLink as="/series" pathname="/series">
              {isShowNewSeries() && <NewTag>NEW</NewTag>}
              {seriesArticleSectionTag}
            </StyledBaseLink>
          </SeriesArticleSectionTag>

          <Headline>{headline}</Headline>

          <SummaryAndActionWidget>
            <Summary>
              {providers.summary({
                isTextOverflowEllipsis: true,
              })}
            </Summary>
            <ActionWidget>
              <Share reference={content} />
              {actionWidget}
            </ActionWidget>
          </SummaryAndActionWidget>
        </BorderedContainer>
        <UpdatedDate>
          Updated:{" "}
          {providers.updatedDate({
            format: "h:mmaaa, dd MMM yyyy",
          })}
        </UpdatedDate>
      </Content>
    </Container>
  );
};

Component.displayName = "ContentItemSeriesBanner";

export const ContentItemSeriesBanner = Component;
